import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Subject, from } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';

@Component({
  selector: 'app-products-doc-pdf-view',
  template: `
    <div class="download-container" *ngIf="errorMessage">
      <p class="error-message">{{ errorMessage }}</p>
    </div>
    <div class="download-container" *ngIf="isDownloading">
      <p>Downloading your document, please wait...</p>
    </div>
  `,
  styles: [`
    .download-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      font-family: Arial, sans-serif;
      font-size: 16px;
    }
    .error-message {
      color: red;
    }
  `],
  standalone: false
})
export class ProductsDocPdfViewComponent implements OnInit, OnDestroy {
  productId: string | null;
  effectiveDate: string | null;
  type: string | null;

  isDownloading = false;
  errorMessage = '';

  private destroy$ = new Subject<void>();

  constructor(private http: HttpClient,
    private httpClient: HTTPWrapperContext
  ) {}

  productTypeMap: Record<string, string> = {
    M: 'MEDICAL',
    V: 'VISION'
  };

  ngOnInit(): void {
    this.extractQueryParams();
    this.validateAndDownload();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Extracts query parameters from the current URL
   */
  private extractQueryParams(): void {
    const params = new URLSearchParams(window.location.search);
    this.productId    = params.get('productId');
    this.effectiveDate = params.get('effectiveDate');
    this.type  = params.get('type');
  }

  /**
   * Validates params then triggers download
   */
  private validateAndDownload(): void {
    if (!this.productId || !this.effectiveDate || !this.type) {
      this.errorMessage = 'Missing required parameters. Please check the URL and try again.';
      console.error('[BenefitGridComponent] Missing required query parameters:', {
        productId: this.productId,
        effectiveDate: this.effectiveDate,
        productType: this.type
      });
      return;
    }

    from(this.makeRequestBlob())
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (response: Blob | null) => {
        this.handleDownloadSuccess(response);
      },
      error: (error) => {
        this.handleDownloadError(error);
      }
    });
  }

  private async makeRequestBlob(): Promise<Blob | null> {
    this.isDownloading = true;
    let payload =  {
      productId: this.productId,
      effectiveDate: this.effectiveDate,
      productType: this.productTypeMap[this.type!] ?? this.type
    };
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      accept: 'application/octet-stream',
      responseType: 'blob',
      url: API_ENDPOINTS.DOCUMENT_INFO.BENIFIT_GRID,
      POST_OR_PUT_PAYLOAD: payload,
      strategyConfigs: { forceModernUrl: true }
    });
  
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    try {
      const response = await this.httpClient.makeRequest(requestConfig) as Blob;
      return response;
    } catch (e) {
      console.error('Failed to fetch SBC blob', e);
      return null;
    }
  }
  

  /**
   * Handles successful blob response — triggers file download then closes tab
   */
  private handleDownloadSuccess(blobResponse: Blob | null): void {
    this.isDownloading = false;
    try {
      if (blobResponse instanceof Blob) {
        this.triggerDownload(blobResponse, 'BenefitGuide.pdf');
        // Show message since window.close() likely won't work
        setTimeout(() => {
          const closed = window.close();
          // Fallback message if tab can't be closed
          this.errorMessage = ''; // clear any errors
        }, 1500);
      } else {
        this.errorMessage = 'Failed to process the document. Please try again.';
      }
    } catch (err) {
      console.error('[ProductsDocPdfView] Error processing download:', err);
      this.errorMessage = 'Failed to process the document. Please try again.';
    }
  }

  /**
   * Creates a temporary anchor element to trigger the file download
   */
  private triggerDownload(blob: Blob, filename: string): void {
    const blobObj = new Blob([blob], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blobObj);

    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  }

  /**
   * Handles HTTP errors
   */
  private handleDownloadError(error: any): void {
    this.isDownloading = false;
    console.error('[BenefitGridComponent] Download failed:', error);

    if (error.status === 404) {
      this.errorMessage = 'Document not found. Please verify the product details.';
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to download this document.';
    } else if (error.status === 0) {
      this.errorMessage = 'Network error. Please check your connection and try again.';
    } else {
      this.errorMessage = `Failed to download document (Error ${error.status}). Please try again.`;
    }
  }
}